import { 
  Search, 
  Calendar, 
  MessageCircle, 
  Car, 
  CheckCircle,
  ArrowRight,
  Clock,
  MapPin,
  Shield,
  Star,
  Users,
  Phone,
  Zap,
  Target,
  Award
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function HowItWorks() {
  const customerSteps = [
    {
      step: "01",
      icon: <Search className="w-8 h-8" />,
      title: "Find Wash Centers",
      description: "Search for nearby wash centers or browse through our curated list of premium service providers near your location.",
      color: "bg-blue-100 text-blue-600",
      details: [
        "Location-based search",
        "Real-time availability",
        "Price comparison",
        "Customer reviews & ratings"
      ]
    },
    {
      step: "02", 
      icon: <Calendar className="w-8 h-8" />,
      title: "Book Your Slot",
      description: "Choose your preferred time slot and service type. Book instantly with just a few taps on your phone.",
      color: "bg-green-100 text-green-600",
      details: [
        "Select date & time",
        "Choose service type",
        "Add special instructions",
        "Confirm booking"
      ]
    },
    {
      step: "03",
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Get WhatsApp Updates",
      description: "Receive booking confirmation, center details, and real-time updates directly on WhatsApp.",
      color: "bg-purple-100 text-purple-600",
      details: [
        "Booking confirmation",
        "Center details & location",
        "Service reminders",
        "Status updates"
      ]
    },
    {
      step: "04",
      icon: <Car className="w-8 h-8" />,
      title: "Drop Your Vehicle",
      description: "Visit the wash center at your scheduled time and drop off your vehicle safely with our partner centers.",
      color: "bg-orange-100 text-orange-600",
      details: [
        "Easy check-in process",
        "Secure vehicle handling",
        "Service tracking",
        "Quality assurance"
      ]
    },
    {
      step: "05",
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Service Complete",
      description: "Get notified when your vehicle is ready. Pay securely and collect your sparkling clean vehicle.",
      color: "bg-green-100 text-green-600",
      details: [
        "Completion notification",
        "Secure payment options",
        "Service feedback",
        "Digital receipt"
      ]
    }
  ];

  const vendorSteps = [
    {
      step: "01",
      icon: <Users className="w-8 h-8" />,
      title: "Register Your Center",
      description: "Sign up as a wash center partner and complete your profile with business details and service offerings.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      step: "02",
      icon: <Target className="w-8 h-8" />,
      title: "Get Verified",
      description: "Our team verifies your business credentials and service quality to ensure customer satisfaction.",
      color: "bg-green-100 text-green-600"
    },
    {
      step: "03",
      icon: <Calendar className="w-8 h-8" />,
      title: "Manage Bookings",
      description: "Receive and manage customer bookings through our dashboard and WhatsApp integration.",
      color: "bg-purple-100 text-purple-600"
    },
    {
      step: "04",
      icon: <Award className="w-8 h-8" />,
      title: "Grow Your Business",
      description: "Access analytics, customer feedback, and marketing tools to grow your wash center business.",
      color: "bg-orange-100 text-orange-600"
    }
  ];

  const features = [
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "WhatsApp Integration",
      description: "All communications happen through WhatsApp - no need to download additional apps.",
      color: "bg-green-100 text-green-600"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Payments",
      description: "Multiple payment options with bank-grade security for all transactions.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Location-Based",
      description: "Find the nearest wash centers with real-time availability and directions.",
      color: "bg-purple-100 text-purple-600"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Real-Time Updates",
      description: "Get instant notifications about booking status, service progress, and completion.",
      color: "bg-orange-100 text-orange-600"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Quality Assured",
      description: "All partner centers are verified and rated by customers for quality service.",
      color: "bg-yellow-100 text-yellow-600"
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "24/7 Support",
      description: "Round-the-clock customer support to help you with any queries or issues.",
      color: "bg-red-100 text-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-green-600/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200 mb-6 inline-flex items-center max-w-full">
              <Zap className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="break-words">How It Works</span>
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 break-words">
              Simple Steps to
              <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent"> Book Your Wash</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto break-words">
              Getting your vehicle washed has never been easier. Follow these simple steps to book your wash 
              and experience the convenience of our platform.
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center max-w-2xl mx-auto">
              <Link to="/booking" className="w-full md:flex-1">
                <Button size="lg" className="w-full h-auto min-h-[52px] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 sm:px-8 py-3.5 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Car className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Start Booking Now</span>
                </Button>
              </Link>
              <Link to="/onboard-vendor" className="w-full md:flex-1">
                <Button size="lg" variant="outline" className="w-full h-auto min-h-[52px] border-2 border-blue-200 text-blue-700 hover:bg-blue-50 px-6 sm:px-8 py-3.5 text-base sm:text-lg font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Users className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Join as Vendor</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Journey */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              For Customers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Book your vehicle wash in just 5 simple steps. It's quick, easy, and convenient.
            </p>
          </div>

          <div className="relative">
            {/* Connection Lines */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-green-200 to-orange-200 z-0"></div>
            
            <div className="grid lg:grid-cols-5 gap-8 relative z-10">
              {customerSteps.map((step, index) => (
                <div key={index} className="flex flex-col items-center">
                  <Card className="w-full max-w-sm group hover:shadow-xl transition-all duration-300 border-0 bg-white">
                    <CardContent className="p-8 text-center">
                      {/* Step Number */}
                      <div className="relative mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto">
                          {step.step}
                        </div>
                        <div className={`w-12 h-12 ${step.color} rounded-lg flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300`}>
                          {step.icon}
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {step.description}
                      </p>
                      
                      {/* Step Details */}
                      <div className="space-y-2">
                        {step.details?.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-center text-sm text-gray-500">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            {detail}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Arrow for mobile */}
                  {index < customerSteps.length - 1 && (
                    <div className="lg:hidden flex justify-center my-4">
                      <ArrowRight className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Journey */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              For Wash Centers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our platform and start growing your wash center business with our comprehensive tools and support.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vendorSteps.map((step, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto">
                      {step.step}
                    </div>
                    <div className={`w-12 h-12 ${step.color} rounded-lg flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300`}>
                      {step.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Our Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've built our platform with features that make vehicle wash booking simple, secure, and convenient for everyone.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white">
                <CardContent className="p-8">
                  <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Benefits for Everyone
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our platform creates value for both customers and wash centers, creating a win-win ecosystem.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">For Customers</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Easy booking in under 2 minutes</li>
                      <li>• WhatsApp integration for updates</li>
                      <li>• Secure payment options</li>
                      <li>• Quality assured services</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">For Wash Centers</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Increased customer reach</li>
                      <li>• Business management tools</li>
                      <li>• Customer analytics</li>
                      <li>• Marketing support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl rounded-3xl overflow-hidden">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {/* Mock WhatsApp Interface */}
                    <div className="bg-gray-900 rounded-2xl p-6 text-white">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">T</span>
                          </div>
                          <div>
                            <div className="font-semibold">Timelyyy</div>
                            <div className="text-xs text-gray-300">Vehicle Wash Booking</div>
                          </div>
                        </div>
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-sm text-gray-300">Your booking is confirmed!</div>
                        </div>
                        <div className="bg-green-600 rounded-lg p-3 ml-8">
                          <div className="text-sm">Wash Center: Premium Car Care</div>
                          <div className="text-xs text-green-100 mt-1">Time: 2:00 PM - 3:00 PM</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-sm text-gray-300">Your vehicle is ready for pickup!</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 break-words">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto break-words">
            Join thousands of satisfied customers and wash center owners who trust Timelyyy for their vehicle wash needs.
          </p>
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center max-w-2xl mx-auto">
            <Link to="/booking" className="w-full md:flex-1">
              <Button size="lg" className="w-full h-auto min-h-[56px] bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                <Car className="w-5 h-5 flex-shrink-0" />
                <span className="whitespace-nowrap">Book Your Wash Now</span>
              </Button>
            </Link>
            <Link to="/onboard-vendor" className="w-full md:flex-1">
              <Button size="lg" className="w-full h-auto min-h-[56px] bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                <Users className="w-5 h-5 flex-shrink-0" />
                <span className="whitespace-nowrap">Join as Vendor</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
